import React from 'react';
import { fmtCurrency } from '../../../utils/formatNumber';
import { formatDate }  from '../../../utils/dateUtils';
import styles from '../MemberReports.module.css';

const memberName   = (o) => o.member?.name || o.userId?.name || '—';
const categoryName = (o) => o.DonationCategory?.name || o.donationCategoryId?.name || '—';

const getLastPaidDate = (offering) => {
  const dates = (offering.settlements || [])
    .map((s) => s.Receipt?.receiptDate)
    .filter(Boolean)
    .sort((a, b) => (a > b ? 1 : -1));
  return dates.length > 0 ? dates[dates.length - 1] : null;
};

export default function MemberContributionsReport({ offerings, viewMode, memberOptions = [], filterFrom = '', filterTo = '' }) {
  const depLabelMap = Object.fromEntries(
    (memberOptions || []).filter((m) => m.badge?.startsWith('Dep.')).map((m) => [String(m.id), m.badge])
  );

  const relevant = offerings
    .filter((o) => Number(o.paidAmount) > 0 && ['partial', 'completed'].includes(o.status))
    .filter((o) => {
      const lastPaid = getLastPaidDate(o);
      if (filterFrom && (!lastPaid || lastPaid < filterFrom)) return false;
      if (filterTo   && (!lastPaid || lastPaid > filterTo))   return false;
      return true;
    })
    .sort((a, b) => {
      const n = memberName(a).localeCompare(memberName(b));
      return n !== 0 ? n : (a.offeringDate || '').localeCompare(b.offeringDate || '');
    });

  const totalPledged = relevant.reduce((s, o) => s + (Number(o.offeredAmount) || 0), 0);
  const totalPaid    = relevant.reduce((s, o) => s + (Number(o.paidAmount)    || 0), 0);

  /* ── Summary ────────────────────────────────────────────────────────────── */
  if (viewMode === 'summary') {
    const grouped = relevant.reduce((acc, o) => {
      const key  = String(o.userId?.id ?? o.userId);
      const name = memberName(o);
      if (!acc[key]) acc[key] = { id: key, name, totalPledged: 0, totalPaid: 0 };
      acc[key].totalPledged += Number(o.offeredAmount) || 0;
      acc[key].totalPaid    += Number(o.paidAmount)    || 0;
      return acc;
    }, {});
    const rows = Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));

    return (
      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Member</th>
                <th className={styles.amtRight}>Total Offered (₹)</th>
                <th className={styles.amtRight}>Total Paid (₹)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={4} className="empty-state">No contributions found.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.id}>
                  <td>{i + 1}</td>
                  <td>
                    <strong>{r.name}</strong>
                    {depLabelMap[r.id] && (
                      <span style={{ fontWeight: 400, fontSize: '0.8em', color: 'var(--color-text-muted)', marginLeft: 6 }}>
                        ({depLabelMap[r.id]})
                      </span>
                    )}
                  </td>
                  <td className={styles.amtRight}>{fmtCurrency(r.totalPledged)}</td>
                  <td className={styles.amtRight}>{fmtCurrency(r.totalPaid)}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={2}><strong>Total ({rows.length} member{rows.length !== 1 ? 's' : ''})</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(totalPledged)}</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(totalPaid)}</strong></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  }

  /* ── Detail (grouped by member) ────────────────────────────────────────── */
  const groups = relevant.reduce((acc, o) => {
    const key = String(o.userId?.id ?? o.userId);
    if (!acc[key]) acc[key] = { id: key, name: memberName(o), entries: [] };
    acc[key].entries.push(o);
    return acc;
  }, {});
  const memberGroups = Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="card">
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Category</th>
              <th>Offered Date</th>
              <th>Paid Date</th>
              <th className={styles.amtRight}>Offered (₹)</th>
              <th className={styles.amtRight}>Paid (₹)</th>
            </tr>
          </thead>
          <tbody>
            {memberGroups.length === 0 && (
              <tr><td colSpan={6} className="empty-state">No contributions found.</td></tr>
            )}
            {memberGroups.map((group) => {
              const gPledged = group.entries.reduce((s, o) => s + (Number(o.offeredAmount) || 0), 0);
              const gPaid    = group.entries.reduce((s, o) => s + (Number(o.paidAmount)    || 0), 0);
              return (
                <React.Fragment key={group.id}>
                  <tr className={styles.memberGroupRow}>
                    <td colSpan={6}>
                      {group.name}
                      {depLabelMap[group.id] && (
                        <span style={{ fontWeight: 400, fontSize: '0.8em', color: 'var(--color-text-muted)', marginLeft: 6 }}>
                          ({depLabelMap[group.id]})
                        </span>
                      )}
                    </td>
                  </tr>
                  {group.entries.map((o, i) => (
                    <tr key={o.id ?? i}>
                      <td style={{ paddingLeft: 20 }}>{i + 1}</td>
                      <td>{categoryName(o)}</td>
                      <td>{formatDate(o.offeringDate)}</td>
                      <td style={{ color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
                        {formatDate(getLastPaidDate(o)) || '—'}
                      </td>
                      <td className={styles.amtRight}>{fmtCurrency(o.offeredAmount)}</td>
                      <td className={styles.amtRight}>{fmtCurrency(o.paidAmount)}</td>
                    </tr>
                  ))}
                  <tr className={styles.memberSubtotalRow}>
                    <td colSpan={4} style={{ paddingLeft: 20 }}>
                      Subtotal — {group.entries.length} entr{group.entries.length !== 1 ? 'ies' : 'y'}
                    </td>
                    <td className={styles.amtRight}>{fmtCurrency(gPledged)}</td>
                    <td className={styles.amtRight}>{fmtCurrency(gPaid)}</td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          {memberGroups.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={3}><strong>Grand Total ({relevant.length} entr{relevant.length !== 1 ? 'ies' : 'y'}, {memberGroups.length} member{memberGroups.length !== 1 ? 's' : ''})</strong></td>
                <td />
                <td className={styles.amtRight}><strong>{fmtCurrency(totalPledged)}</strong></td>
                <td className={styles.amtRight}><strong>{fmtCurrency(totalPaid)}</strong></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
